/**
 * Candles for the stock chart, made from the market data the server hands out. The server knows a few
 * candle sizes (1m 5m 15m 1h 1D 1W 1M); the others a trader asks for are put together from them here
 * (two 5-minute candles make a 10-minute one, two or three days make a 2D / 3D candle).
 */
import type { OhlcBar } from '@shared/types'

/** Candles, oldest first. */
export interface Bars {
  o: number[]
  h: number[]
  l: number[]
  c: number[]
  v: number[]
  labels: string[]
  rsi: number[]
  /** For candles inside a day: the part of the trading day each one belongs to (New York time). */
  session?: Session[]
  /** Where the row of candles has a hole: before candle `at` there is trading we have no candles for. */
  holes?: { at: number; text: string }[]
}

/** Before the opening bell, the regular session, after the close. */
export type Session = 'pre' | 'regular' | 'after'
const clock = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false })
/** Which part of the trading day a moment falls in: the regular session runs 09:30–16:00 in New York. */
export function sessionOf(unix: number): Session {
  const [h, m] = clock.format(new Date(unix * 1000)).split(':').map(Number)
  const at = (h % 24) * 60 + m
  return at < 570 ? 'pre' : at >= 960 ? 'after' : 'regular'
}

/** How much time one candle holds. */
export type Interval = '1m' | '5m' | '10m' | '1h' | '2h' | '1D' | '2D' | '3D' | '1W' | '1M'
export const INTERVALS: Interval[] = ['1m', '5m', '10m', '1h', '2h', '1D', '2D', '3D', '1W', '1M']
/** What to ask the server for, and how many of those make one candle. */
export const SOURCE: Record<Interval, { timeframe: string; group: number }> = {
  '1m': { timeframe: '1m', group: 1 },
  '5m': { timeframe: '5m', group: 1 },
  '10m': { timeframe: '5m', group: 2 },
  '1h': { timeframe: '1h', group: 1 },
  '2h': { timeframe: '1h', group: 2 },
  '1D': { timeframe: '1D', group: 1 },
  '2D': { timeframe: '1D', group: 2 },
  '3D': { timeframe: '1D', group: 3 },
  '1W': { timeframe: '1W', group: 1 },
  '1M': { timeframe: '1M', group: 1 },
}

/** Wilder's RSI(14) over closes. */
function wilder(c: number[]) {
  const out = Array<number>(c.length).fill(50)
  let g = 0
  let l = 0
  for (let i = 1; i < c.length; i++) {
    const ch = c[i] - c[i - 1]
    g = (g * 13 + Math.max(ch, 0)) / 14
    l = (l * 13 + Math.max(-ch, 0)) / 14
    out[i] = l ? 100 - 100 / (1 + g / l) : 100
  }
  return out
}

const label = (t: number | string, intraday: boolean) => {
  const d = typeof t === 'number' ? new Date(t * 1000) : new Date(`${t}T12:00:00`)
  return intraday ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

/** The server's bars as the chart draws them, `group` of them to a candle (counted from the newest, so the last candle is whole). */
export function toBars(raw: OhlcBar[], group = 1): Bars {
  const intraday = typeof raw[0]?.time === 'number'
  const out: Bars = { o: [], h: [], l: [], c: [], v: [], labels: [], rsi: [] }
  const lead = raw.length % group
  for (let i = lead; i < raw.length; i += group) {
    const part = raw.slice(i, i + group)
    out.o.push(part[0].open)
    out.h.push(Math.max(...part.map((b) => b.high)))
    out.l.push(Math.min(...part.map((b) => b.low)))
    out.c.push(part[part.length - 1].close)
    out.v.push(part.reduce((a, b) => a + (b.volume ?? 0), 0))
    out.labels.push(label(part[part.length - 1].time, intraday))
    if (intraday) (out.session ??= []).push(sessionOf(Number(part[0].time)))
  }
  out.rsi = wilder(out.c)
  if (intraday) out.holes = holesIn(raw, lead, group)
  return out
}

const HOLE = 45 * 60
const hm = (unix: number) => new Date(unix * 1000).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })
const nyMinutes = (unix: number) => {
  const [h, m] = clock.format(new Date(unix * 1000)).split(':').map(Number)
  return (h % 24) * 60 + m
}

/**
 * A chart of trading leaves the night out — but it must not leave out trading it simply does not have. A hole is
 * where candles are missing inside the trading day: the day's first candle comes long after that day began (04:00
 * New York where the row carries pre-market candles at all, else the opening bell), or two candles of one day lie
 * far apart. The chart marks it instead of drawing the two sides as neighbours.
 */
export function holesIn(raw: OhlcBar[], lead = 0, group = 1): { at: number; text: string }[] {
  const out: { at: number; text: string }[] = []
  const pre = raw.some((b) => typeof b.time === 'number' && sessionOf(b.time) === 'pre')
  for (let i = Math.max(1, lead); i < raw.length; i++) {
    const t = Number(raw[i].time)
    const before = Number(raw[i - 1].time)
    let from: number | null = null
    if (marketDay(t) !== marketDay(before)) {
      const begins = t - (nyMinutes(t) - (pre ? 240 : 570)) * 60
      if (t - begins > HOLE) from = begins
    } else if (t - before > HOLE && !(sessionOf(before) === 'regular' && sessionOf(t) !== 'regular' && !pre)) from = before
    if (from != null) out.push({ at: Math.floor((i - lead) / group), text: `No data ${hm(from)} – ${hm(t)}` })
  }
  return out.filter((h, k) => h.at > 0 && out.findIndex((x) => x.at === h.at) === k)
}

/** The day a moment falls on in New York — the market's own calendar. */
export const marketDay = (unix: number) => new Date(unix * 1000).toLocaleDateString('en-CA', { timeZone: 'America/New_York' })

/**
 * The candles the server handed out, grown by what has traded since: `minutes` are the one-minute candles the live
 * feed has recorded (pre- and after-market too), `price` the newest trade. Always made afresh from the server's own
 * candles, so nothing is counted twice however often it is called.
 *
 * Inside a day (`secs` = the length of one of the server's candles): a minute that falls in the last candle moves it;
 * later minutes open new candles, in step with the last one. Daily candles: today's minutes move today's candle, or
 * open it when the server does not have it yet. Weekly and monthly candles are left as they came.
 */
export function growBars(raw: OhlcBar[], minutes: OhlcBar[], secs: number | null, price?: number | null, daily = false): OhlcBar[] {
  if (!raw.length || (!secs && !daily)) return raw
  const out = raw.map((b) => ({ ...b }))
  const live = minutes.filter((m) => typeof m.time === 'number').sort((a, b) => Number(a.time) - Number(b.time))
  const fold = (into: OhlcBar, m: OhlcBar) => {
    into.high = Math.max(into.high, m.high)
    into.low = Math.min(into.low, m.low)
    into.close = m.close
  }
  let moved = false
  if (secs) {
    const start = Number(out[out.length - 1].time)
    if (!Number.isFinite(start)) return raw
    for (const m of live) {
      const t = Number(m.time)
      const last = out[out.length - 1]
      const from = Number(last.time)
      if (t < start) continue // (already inside what the server gave)
      if (t < from + secs) {
        // the minute the server's last candle ended on is known to it already; anything newer moves the candle
        if (from === start && secs === 60) Object.assign(last, { open: m.open, high: m.high, low: m.low, close: m.close, volume: m.volume ?? last.volume })
        else fold(last, m)
      } else out.push({ time: from + Math.floor((t - from) / secs) * secs, open: m.open, high: m.high, low: m.low, close: m.close, volume: m.volume ?? 0 })
      moved = true
    }
  } else {
    const newest = live[live.length - 1]
    if (newest) {
      const today = marketDay(Number(newest.time))
      const mine = live.filter((m) => marketDay(Number(m.time)) === today)
      const last = out[out.length - 1]
      if (String(last.time) === today) {
        for (const m of mine) fold(last, m)
        moved = true
      } else if (String(last.time) < today) {
        const bar: OhlcBar = { time: today, open: mine[0].open, high: mine[0].high, low: mine[0].low, close: mine[0].close, volume: 0 }
        for (const m of mine) (fold(bar, m), (bar.volume = (bar.volume ?? 0) + (m.volume ?? 0)))
        out.push(bar)
        moved = true
      }
    }
  }
  // The newest trade moves the newest candle — but only a candle the live feed is in (never yesterday's with today's price).
  if (moved && price != null && price > 0) {
    const last = out[out.length - 1]
    last.close = price
    last.high = Math.max(last.high, price)
    last.low = Math.min(last.low, price)
  }
  return moved ? out : raw
}

/** A simple moving average over closes (over what there is, for the first few). */
export function movingAverage(c: number[], n: number): number[] {
  const out: number[] = []
  let sum = 0
  for (let i = 0; i < c.length; i++) {
    sum += c[i]
    if (i >= n) sum -= c[i - n]
    out.push(sum / Math.min(i + 1, n))
  }
  return out
}
